import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const COLORS = {
  idle: "#64748b",
  en_route: "#0ea5e9",
  picked_up: "#16a34a",
  finished: "#047857",
};

function icon(status, label) {
  const color = COLORS[status] || "#64748b";
  return L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center">
      <div style="background:${color};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 28],
  });
}

const MAP_STYLE = { height: 420, width: "100%" };
const TRACK_LINE = { color: "#7c3aed", weight: 4, opacity: 0.85 };
const trackPoint = (first, last) => ({
  color: "#fff", weight: 2, fillOpacity: 1,
  fillColor: first ? "#16a34a" : last ? "#dc2626" : "#7c3aed",
});

function driverIcon(name, status) {
  const color = COLORS[status] || "#64748b";
  const initials = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;gap:2px">
      <div style="background:#fff;color:${color};border:2.5px solid ${color};width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font:700 12px/1 system-ui;box-shadow:0 2px 6px rgba(0,0,0,.3)">${initials}</div>
      <span style="background:${color};color:#fff;font:600 9px/1 system-ui;padding:2px 5px;border-radius:8px;white-space:nowrap">${name}</span>
    </div>`,
    iconSize: [34, 48],
    iconAnchor: [17, 24],
  });
}

function FitBounds({ positions, maxZoom = 12 }) {
  const map = useMap();
  const sig = positions.map((p) => `${p.lat},${p.lon}`).join("|");
  useEffect(() => {
    if (positions.length) {
      const bounds = L.latLngBounds(positions.map((p) => [p.lat, p.lon]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom });
    }
    /* eslint-disable-next-line */
  }, [sig, maxZoom, map]);
  return null;
}

export function FleetMap({ positions, drivers = [], showDrivers = true, track = null }) {
  const center = positions.length ? [positions[0].lat, positions[0].lon] : [47.322, 5.041];
  const tp = track?.points || [];
  const all = tp.length ? tp : (showDrivers ? [...positions, ...drivers] : positions);
  return (
    <div className="rounded-md overflow-hidden border border-border" data-testid="fleet-map">
      <MapContainer center={center} zoom={11} style={MAP_STYLE} scrollWheelZoom={true}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {positions.map((p) => (
          <Marker key={p.vehicle_id} position={[p.lat, p.lon]} icon={icon(p.status, p.label)}>
            <Popup>
              <div className="text-xs">
                <p className="font-semibold">{p.label}</p>
                <p>{p.status === "idle" ? "Au dépôt / disponible" : p.live ? "Position en direct" : "Dernière position connue"}</p>
                {p.at && p.source === "gps" && (
                  <p className="text-[10px] opacity-70">GPS reçu à {new Date(p.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
                )}
                {p.current && <p className="mt-1">{p.current.time} · {p.current.patient} → {p.current.to}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
        {showDrivers && drivers.map((d) => (
          <Marker key={d.driver_id} position={[d.lat, d.lon]} icon={driverIcon(d.name, d.status)} zIndexOffset={500}>
            <Popup>
              <div className="text-xs">
                <p className="font-semibold">{d.name}</p>
                <p>{d.status === "idle" ? "Disponible / hors course" : d.status === "en_route" ? "En route vers le patient" : "Patient à bord"}</p>
                {d.at && (
                  <p className="text-[10px] opacity-70">
                    GPS du téléphone à {new Date(d.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                )}
                <p className="text-[10px] opacity-70">{d.rides_today} course(s) aujourd'hui</p>
                {d.current && <p className="mt-1">{d.current.time} · {d.current.patient} → {d.current.to}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
        {tp.length > 1 && (
          <Polyline positions={tp.map((p) => [p.lat, p.lon])} pathOptions={TRACK_LINE} />
        )}
        {tp.map((p, i) => (
          <CircleMarker
            key={`${p.at}-${i}`}
            center={[p.lat, p.lon]}
            radius={i === 0 || i === tp.length - 1 ? 7 : 4}
            pathOptions={trackPoint(i === 0, i === tp.length - 1)}
          >
            <Tooltip>
              {i === 0 ? "Départ · " : i === tp.length - 1 ? "Dernier point · " : ""}
              {new Date(p.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </Tooltip>
          </CircleMarker>
        ))}
        <FitBounds positions={all} maxZoom={tp.length ? 14 : 12} />
      </MapContainer>
    </div>
  );
}
